import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '@/components/AppHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pencil, Trash2, Check, X } from 'lucide-react';
import {
  deleteTemplate,
  listTemplates,
  renameTemplate,
} from '@/lib/api/tournament-templates';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import type { TournamentTemplate } from '@swiss-magic/shared-types';

// Story 078 — director-private templates management page. Lists
// the director's templates with rename + delete. No "edit values"
// affordance; directors save a new template from the tournament
// form instead.

export function TemplatesPage() {
  const { t } = useTranslation('director');
  const [templates, setTemplates] = useState<TournamentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [pendingDelete, setPendingDelete] = useState<TournamentTemplate | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    listTemplates()
      .then((list) => {
        if (!cancelled) setTemplates(list);
      })
      .catch((err) => {
        if (cancelled) return;
        showErrorToast(
          err instanceof ApiError
            ? err.userMessage
            : t('templates_page.load_failed_toast'),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  function startRename(tpl: TournamentTemplate) {
    setRenamingId(tpl.id);
    setRenameDraft(tpl.name);
  }

  function cancelRename() {
    setRenamingId(null);
    setRenameDraft('');
  }

  async function confirmRename(tpl: TournamentTemplate) {
    const next = renameDraft.trim();
    if (!next || next === tpl.name) {
      cancelRename();
      return;
    }
    try {
      const updated = await renameTemplate(tpl.id, { name: next });
      setTemplates((prev) =>
        prev.map((p) => (p.id === updated.id ? updated : p)),
      );
      cancelRename();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        showErrorToast(t('templates_page.rename_conflict_toast'));
        return;
      }
      showErrorToast(
        err instanceof ApiError
          ? err.userMessage
          : t('templates_page.rename_failed_toast'),
      );
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    const tpl = pendingDelete;
    try {
      await deleteTemplate(tpl.id);
      setTemplates((prev) => prev.filter((p) => p.id !== tpl.id));
      showSuccessToast(t('templates_page.delete_success_toast'));
    } catch (err) {
      showErrorToast(
        err instanceof ApiError
          ? err.userMessage
          : t('templates_page.delete_failed_toast'),
      );
    } finally {
      setPendingDelete(null);
    }
  }

  return (
    <div
      data-testid="templates-page"
      className="min-h-dvh bg-background pb-[env(safe-area-inset-bottom)]"
    >
      <AppHeader />
      <main className="mx-auto max-w-3xl px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">{t('templates_page.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">
                {t('templates_page.loading')}
              </p>
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('templates_page.empty')}
              </p>
            ) : (
              <ul className="divide-y">
                {templates.map((tpl) => (
                  <li
                    key={tpl.id}
                    data-testid={`template-row-${tpl.name}`}
                    className="flex items-center gap-2 py-3"
                  >
                    {renamingId === tpl.id ? (
                      <>
                        <Input
                          data-testid={`rename-template-input-${tpl.name}`}
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          className="flex-1"
                          autoFocus
                        />
                        <Button
                          size="icon"
                          variant="outline"
                          data-testid={`rename-template-confirm-${tpl.name}`}
                          onClick={() => confirmRename(tpl)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={cancelRename}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm">{tpl.name}</span>
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`rename-template-button-${tpl.name}`}
                          onClick={() => startRename(tpl)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`delete-template-button-${tpl.name}`}
                          onClick={() => setPendingDelete(tpl)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </main>

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
      >
        <DialogContent data-testid="delete-template-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t('templates_page.delete_dialog_title')}</DialogTitle>
            <DialogDescription>
              {pendingDelete
                ? t('templates_page.delete_dialog_description', {
                    name: pendingDelete.name,
                  })
                : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="delete-template-cancel-button"
              onClick={() => setPendingDelete(null)}
            >
              {t('templates_page.delete_cancel')}
            </Button>
            <Button
              variant="destructive"
              data-testid="delete-template-confirm-button"
              onClick={confirmDelete}
            >
              {t('templates_page.delete_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
